import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { pushAlert } from 'modules/login';
import { RepliconOAuth } from '../oauth/RepliconOAuth';
import { useOAuth } from '../oauth/useOAuth';
import { Session } from './types/Session';
import { RepliconSession } from './RepliconSession';
import { SessionEndReason } from './types/InteractiveSession';

const provider = new RepliconOAuth();
const interactiveSession = new RepliconSession();

export const SessionContext = createContext<Session>({
  isInitialized: false,
  hasValidToken: false,
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
  expire: () => {},
  tokenRef: { current: null },
  provider: provider,
  interactiveSession: { current: null },
  checkIsValidSession: async () => undefined,
  disableExtendSessionOnFocus: false,
  setDisableExtendSessionOnFocus: () => {}
});

export const SessionContextProvider: React.FunctionComponent = ({
  children
}: {
  children?: React.ReactNode;
}) => {
  const { isInitialized, hasValidToken, tokenRef, login, logout } = useOAuth(
    provider
  );

  const sessionRef = useRef(interactiveSession);
  const [
    disableExtendSessionOnFocus,
    setDisableExtendSessionOnFocus
  ] = useState(false);

  const doLogout = useCallback(async () => {
    if (interactiveSession) {
      await interactiveSession.end(SessionEndReason.logout);
    }

    logout();
  }, [logout]);

  const doExpire = useCallback(async () => {
    if (interactiveSession) {
      await interactiveSession.end(SessionEndReason.expired);
    }

    logout();
  }, [logout]);

  const checkIsValidSession = useCallback(async () => {
    return await interactiveSession.validate();
  }, []);

  useEffect(() => {
    const sessionEnded = async (event: any) => {
      if (event && event.reason === SessionEndReason.no_substitutes) {
        pushAlert({
          severity: 'info',
          message:
            'Substitute user login is not allowed. Please login to continue.',
          dismissible: false
        });
      }
      logout(
        event && event.reason === SessionEndReason.expired
          ? SessionEndReason.expired
          : null
      );
    };

    interactiveSession.addEventListener('ended', sessionEnded as EventListener);

    return () => {
      interactiveSession.removeEventListener(
        'ended',
        sessionEnded as EventListener
      );
    };
  }, [doLogout, logout]);

  const session = useMemo<Session>(
    () => ({
      isInitialized,
      hasValidToken,
      isAuthenticated: isInitialized && hasValidToken,
      tokenRef,
      login,
      logout: doLogout,
      expire: doExpire,
      provider,
      interactiveSession: sessionRef,
      checkIsValidSession,
      disableExtendSessionOnFocus: disableExtendSessionOnFocus,
      setDisableExtendSessionOnFocus: setDisableExtendSessionOnFocus
    }),
    [
      isInitialized,
      hasValidToken,
      tokenRef,
      login,
      doLogout,
      doExpire,
      checkIsValidSession,
      disableExtendSessionOnFocus
    ]
  );

  return (
    <SessionContext.Provider value={session}>
      {children}
    </SessionContext.Provider>
  );
};
